// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React from "react";
import { CircleX, User, Package, Calendar, Users, Loader2 } from "lucide-react";

const Bookings = ({ booking, isSelected, onClick }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
      case "confirmed":
      case "paid":
        return "bg-green-100 text-green-800";
      case "pending":
      case "waiting":
      case "payment-pending":
        return "bg-yellow-100 text-yellow-800";
      case "draft":
      case "submitted":
      case "processed":
        return "bg-blue-100 text-blue-800";
      case "rejected":
      case "canceled":
      case "payment-error":
        return "bg-red-100 text-red-800";
      case "on-hold":
      case "postponed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getLlmLabelInfo = (llmLabel) => {
    switch (llmLabel) {
      case "U":
        return {
          label: "Unbedenklich",
          className:
            "inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold border bg-emerald-100 text-emerald-800 border-emerald-200",
        };
      case "E":
        return {
          label: "Explizit problematisch",
          className:
            "inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold border bg-rose-100 text-rose-800 border-rose-200",
        };
      case "V":
        return {
          label: "Subtile/kodierte Hinweise",
          className:
            "inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold border bg-amber-100 text-amber-800 border-amber-200",
        };
      default:
        return {
          label: "N/A",
          className:
            "inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold border bg-gray-100 text-gray-800 border-gray-200",
        };
    }
  };

  const getUserDisplayName = () => {
    if (booking.user) {
      if (booking.user.first_name && booking.user.last_name) {
        return `${booking.user.first_name} ${booking.user.last_name}`;
      }
      if (booking.user.first_name) {
        return booking.user.first_name;
      }
      if (booking.user.last_name) {
        return booking.user.last_name;
      }
      if (booking.user.name) {
        return booking.user.name;
      }
    }
    return booking.contactPerson || booking.email || "N/A";
  };

  const hasValidLlmResults =
    booking.llm_general === true ||
    booking.llm_general === false ||
    booking.llm_general === 0 ||
    booking.llm_general === 1;
  const llmLabelInfo = getLlmLabelInfo(booking.llm_label);

  return (
    <div
      className={`border-2 rounded-xl p-5 cursor-pointer transition-all duration-200 ${
        isSelected
          ? "hover:shadow-md border-blue-300 bg-blue-50 shadow-lg shadow-blue-500/10"
          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
      }`}
      onClick={() => onClick(booking)}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h4 className="text-lg font-semibold text-slate-900">
              {booking.title || "N/A"}
            </h4>
            <span
              className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold border ${getStatusColor(booking.status)}`}
            >
              {booking.status || "draft"}
            </span>
          </div>
          <p className="text-sm text-slate-500 mb-2 font-mono">
            {booking.bookingNumber || "N/A"}
          </p>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <span className="text-sm text-slate-600 mb-3">
              <User className="lucide lucide-user w-4 h-4 inline mr-2" />
              {getUserDisplayName()}
            </span>
            {booking.organization?.name && (
              <span className="flex items-center gap-2 text-sm text-slate-500 mb-2 font-mono">
                <Users className="w-4 h-4" />
                {booking.organization.name}
              </span>
            )}
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {booking.timeSlot?.startTime
                ? new Date(booking.timeSlot.startTime).toLocaleDateString()
                : "No date"}
            </span>
            <span className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              {booking.participants || "N/A"}
            </span>
            <span className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Paket {booking.package || "N/A"}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-3">
          {hasValidLlmResults ? (
            <div className={llmLabelInfo.className}>
              <CircleX className="lucide lucide-circle-x w-3 h-3 mr-1" />
              {llmLabelInfo.label}
            </div>
          ) : (
            <div className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold border bg-slate-100 text-slate-600 border-slate-200">
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              processing...
            </div>
          )}
          <span className="text-lg font-bold text-slate-900">
            €{booking.total || "N/A"}
          </span>
        </div>
      </div>
    </div>
  );
};

export default Bookings;
